import React, { useEffect, useState } from "react";
import { IoGlobeOutline, IoSearch } from "react-icons/io5";
import { FiTrash2, FiEdit } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import Pagination from "@mui/material/Pagination";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useAuth from "../../useAuth";
import { DAY, server } from "../../server";
import { useDebounce } from "../../hooks/useDebounce";
import LoadingComponent from "../../Components/LoadingComponent";

export default function DomainsList() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [domainsList, setDomainsList] = useState([]);
  const [domainsCount, setDomainsCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [searchParams, setSearchParams] = useState("");
  const [editingDomain, setEditingDomain] = useState(null);
  const [editFormData, setEditFormData] = useState({
    domainName: "",
    ipAddress: "",
    expirationDate: "",
    alertDate: "",
    remarks: "",
  });

  const debounceValue = useDebounce(searchParams, 500);

  useEffect(() => {
    getAllDomains();
  }, [pageNo, pageSize, debounceValue]);

  const getAllDomains = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/domain/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          pageNo,
          pageSize,
          searchParams,
          deleted: false,
        },
      });

      setDomainsList(data?.data || []);
      setDomainsCount(data?.count || 0);
      setLoading(false);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message);
      setLoading(false);
    }
  };

  const handleDeleteDomain = async (domainId) => {
    const userRes = window.confirm(
      "Are you sure you want to delete this domain?",
    );
    if (!userRes) return;

    try {
      setLoading(true);
      await axios.delete(`${server}/domain/deleteById`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          domainId,
        },
      });
      toast.success("Domain deleted successfully!");
      getAllDomains();
      setLoading(false);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message);
      setLoading(false);
    }
  };

  const handleEditClick = (domain) => {
    setEditingDomain(domain);
    setEditFormData({
      domainName: domain.domainName,
      ipAddress: domain.ipAddress,
      expirationDate: domain.expirationDate
        ? new Date(domain.expirationDate).toISOString().split("T")[0]
        : "",
      alertDate: domain.alertDate
        ? new Date(domain.alertDate).toISOString().split("T")[0]
        : "",
      remarks: domain.remarks || "",
    });
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = {
        domainName: editFormData.domainName,
        ipAddress: editFormData.ipAddress,
        expirationDate: editFormData.expirationDate
          ? new Date(editFormData.expirationDate).getTime()
          : null,
        alertDate: editFormData.alertDate
          ? new Date(editFormData.alertDate).getTime()
          : null,
        remarks: editFormData.remarks,
      };

      await axios.put(
        `${server}/domain/updateById?domainId=${editingDomain.id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      toast.success("Domain updated successfully!");
      setEditingDomain(null);
      getAllDomains();
      setLoading(false);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen p-6">
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="max-w-6xl mx-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <IoGlobeOutline className="text-3xl text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-800">Domains List</h1>
          </div>

          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search domains..."
              value={searchParams}
              onChange={(e) => setSearchParams(e.target.value)}
              className="pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-300 focus:outline-none"
            />
            <IoSearch className="absolute left-3 top-2.5 text-gray-400" />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto bg-white rounded-xl shadow border border-gray-200">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 border-b">
              <tr>
                <th className="text-left p-3 font-semibold text-gray-600">#</th>
                <th className="text-left p-3 font-semibold text-gray-600">
                  Domain Name
                </th>
                <th className="text-left p-3 font-semibold text-gray-600">
                  IP Address
                </th>
                <th className="text-left p-3 font-semibold text-gray-600">
                  Expiration Date
                </th>
                <th className="text-left p-3 font-semibold text-gray-600">
                  Alert Date
                </th>
                <th className="text-left p-3 font-semibold text-gray-600">
                  Expires In
                </th>
                <th className="text-center p-3 font-semibold text-gray-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {domainsList.length > 0 ? (
                domainsList.map((domain, index) => (
                  <tr
                    key={domain.id}
                    className={`border-b hover:bg-gray-50 transition ${
                      (domain.expirationDate - Date.now()) / DAY < 7 &&
                      "bg-rose-300"
                    } ${
                      (domain.expirationDate - Date.now()) / DAY < 30 &&
                      "bg-amber-300"
                    }`}
                  >
                    <td className="p-3 text-gray-700">
                      {(pageNo - 1) * pageSize + index + 1}
                    </td>
                    <td className="p-3 text-gray-800 font-medium">
                      {domain.domainName}
                    </td>
                    <td className="p-3 text-gray-800 font-medium">
                      {domain.ipAddress}
                    </td>
                    <td className="p-3 text-gray-600">
                      {domain.expirationDate
                        ? new Date(domain.expirationDate).toLocaleDateString(
                            "en-GB",
                            {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            },
                          )
                        : "—"}
                    </td>
                    <td className="p-3 text-gray-600">
                      {domain.alertDate
                        ? new Date(domain.alertDate).toLocaleDateString(
                            "en-GB",
                            {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            },
                          )
                        : "—"}
                    </td>
                    <td className="p-3 text-gray-600 font-semibold">
                      {((domain.expirationDate - Date.now()) / DAY).toFixed(
                        0,
                      ) || "—"}{" "}
                      Days
                    </td>
                    <td className="p-3 text-center flex justify-center gap-2">
                      <button
                        className="p-2 rounded-full hover:bg-blue-100 text-blue-600 transition"
                        onClick={() => handleEditClick(domain)}
                      >
                        <FiEdit className="text-lg" />
                      </button>
                      <button
                        className="p-2 rounded-full hover:bg-red-100 text-red-600 transition"
                        onClick={() => handleDeleteDomain(domain?.id)}
                      >
                        <FiTrash2 className="text-lg" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="p-5 text-center text-gray-500">
                    No domains found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {domainsCount > 0 && (
          <div className="flex justify-end mt-6">
            <Pagination
              count={Math.ceil(domainsCount / pageSize)}
              page={pageNo}
              onChange={(event, value) => setPageNo(value)}
              color="primary"
              shape="rounded"
            />
          </div>
        )}
      </motion.div>

      {/* Edit Modal */}
      <AnimatePresence>
        {editingDomain && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-lg w-full max-w-lg p-6"
            >
              <h2 className="text-2xl font-bold mb-4">Edit Domain</h2>
              <form onSubmit={handleUpdateSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Domain Name
                  </label>
                  <input
                    type="text"
                    required
                    value={editFormData.domainName}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        domainName: e.target.value,
                      })
                    }
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    IP Address
                  </label>
                  <input
                    type="text"
                    value={editFormData.ipAddress}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        ipAddress: e.target.value,
                      })
                    }
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Expiration Date
                    </label>
                    <input
                      type="date"
                      value={editFormData.expirationDate}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          expirationDate: e.target.value,
                        })
                      }
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Alert Date
                    </label>
                    <input
                      type="date"
                      value={editFormData.alertDate}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          alertDate: e.target.value,
                        })
                      }
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Remarks
                  </label>
                  <textarea
                    rows="3"
                    value={editFormData.remarks}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        remarks: e.target.value,
                      })
                    }
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  ></textarea>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setEditingDomain(null)}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                  >
                    Update
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {loading && <LoadingComponent />}
    </div>
  );
}
